// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { Dropdown } from "react-bootstrap";

import { isSet } from "utils";
import { IState } from "states/states";
import { IAction } from "actions";

interface ISearchableDropdownExtProps<ItemType> {
  caption?: string;
  valueList: ItemType[];
  mapperFunction: (value: ItemType | undefined) => void;
  labelFunction: (value: ItemType) => string;
  curItem: string | JSX.Element | undefined;
  nextElementId?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

interface ISearchableDropdownProps {}

const mapStateToProps = (state: IState) => {
  const props: ISearchableDropdownProps = {};
  return props;
};

interface ISearchableDropdownState<ItemType> {
  isOpen: boolean;
  searchText: string;
  selectedItem?: ItemType;
}

interface ISearchableDropdownDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ISearchableDropdownDispatch = {};
  return dispatchProps;
};

class SearchableDropdownComponent<ItemType> extends React.Component<
  ISearchableDropdownProps & ISearchableDropdownExtProps<ItemType> & ISearchableDropdownDispatch,
  ISearchableDropdownState<ItemType>
> {
  public state: ISearchableDropdownState<ItemType> = {
    isOpen: false,
    searchText: "",
  };

  private onKeyEvent(event: React.KeyboardEvent) {
    // handle special events
    if (event.key === "Escape") {
      if (this.state.isOpen && this.state.searchText.length > 0) {
        // clear input
        this.setState({ searchText: "", selectedItem: undefined });
      } else if (this.state.isOpen) {
        // close dropdown and save selection
        this.setState({ isOpen: false });
        this.props.mapperFunction(this.state.selectedItem);
      }
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    if (event.key === "Tab") {
      if (this.state.isOpen) {
        // focus button, if given
        if (this.props.nextElementId) {
          const nextElement = document.getElementById(this.props.nextElementId);
          nextElement?.removeAttribute("disabled"); // remove disabled attribute to allow focus
          nextElement?.focus();
          event.stopPropagation();
          event.preventDefault();
        }
        // close dropdown and report selection to parent component
        this.setState({ isOpen: false, searchText: "" });
        this.props.mapperFunction(this.state.selectedItem);
      }
      return;
    }

    // valid character input?
    if (
      (this.state.isOpen && ["Backspace", "Delete"].includes(event.key)) ||
      event.key.length === 1
    ) {
      let newName = this.state.searchText;
      if (event.key === "Backspace" || event.key === "Delete") {
        newName = newName.substring(0, newName.length - 1);
      } else if (event.key.length === 1) {
        newName += event.key.toLowerCase();
      }
      this.setState({ isOpen: true, searchText: newName });
      const newValues = this.getFilteredValueList(newName);
      this.setState({ selectedItem: newValues.length ? newValues[0] : undefined });
      // ensure valid selection after input
      if (newValues.length > 0) {
        document.getElementById(`searchable_${newValues[0]}`)?.focus();
      } else {
        document.getElementById("searchable-dropdown-toggle")?.focus();
      }
      event.stopPropagation();
      event.preventDefault();
    }
  }

  private getFilteredValueList(filter: string): ItemType[] {
    return this.props.valueList.filter(
      (curItem) =>
        filter.length === 0 || this.props.labelFunction(curItem).toLowerCase().includes(filter),
    );
  }

  private getDropdownItems() {
    const valueList = this.getFilteredValueList(this.state.searchText);
    const dropdownItems = valueList.map((curValue) => {
      return (
        <Dropdown.Item
          key={`${curValue}`}
          id={`searchable_${curValue}`}
          onClick={() => this.props.mapperFunction(curValue)}
          onFocus={() => this.setState({ selectedItem: curValue })}
        >
          {this.props.labelFunction(curValue)}
        </Dropdown.Item>
      );
    });
    return dropdownItems;
  }

  public render() {
    const items = this.getDropdownItems();
    return (
      <Dropdown
        className={this.props.className}
        show={this.state.isOpen}
        onToggle={(isOpen) => {
          if (!isOpen) {
            // update selection on close event
            this.props.mapperFunction(this.state.selectedItem);
            this.setState({ searchText: "", selectedItem: undefined });
          }
          this.setState({ isOpen });
        }}
      >
        <Dropdown.Toggle
          variant="secondary"
          id="searchable-dropdown-toggle"
          style={{ marginTop: -2, ...this.props.style }}
          className={this.props.disabled ? "disabled" : undefined}
          onKeyDown={(event) => this.onKeyEvent(event)}
          disabled={this.props.disabled}
        >
          {this.state.isOpen
            ? this.state.searchText.length > 0
              ? this.state.searchText
              : this.props.caption
            : this.props.curItem ?? this.props.caption}
        </Dropdown.Toggle>
        <Dropdown.Menu onKeyDown={(event) => this.onKeyEvent(event)}>
          {isSet(this.props.caption) && <Dropdown.Header>{this.props.caption}</Dropdown.Header>}
          {items}
        </Dropdown.Menu>
      </Dropdown>
    );
  }
}

export function SearchableDropdown<ItemType>(
  props: ISearchableDropdownExtProps<ItemType> & ISearchableDropdownProps,
) {
  const ConnectedComponent = connect(
    mapStateToProps,
    mapDispatchToProps,
  )(SearchableDropdownComponent<ItemType>);

  return <ConnectedComponent {...props} />;
}
export default SearchableDropdown;
